import Col from "@visa/vds/col";
import Grid from "@visa/vds/grid";
import Icon from "@visa/vds/icon";
import Input from "@visa/vds/input";
import InputOption from "@visa/vds/input-option";
import Form from "@visa/vds/form";
import FormControl from "@visa/vds/form-control";
import Row from "@visa/vds/row";
import Select from "@visa/vds/select";
import Td from "@visa/vds/td";
import Tr from "@visa/vds/tr";
import Typography from "@visa/vds/typography";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { useHistory, useLocation } from "react-router-dom";
import MCSLoading from "../../../components/MCSLoading";
import MCSPagedTable from "../../../components/MCSPagedTable";
import OrderStatus from "../../../components/order/OrderStatus";
import { GlobalAlertContext } from "../../../context/GlobalAlertContext";
import { adminService } from "../../../services/admin.service";
import { AdminRoutes } from "../../../_constants/PageRoutes";
import { DateHelper } from "../../../_helpers/DateHelper";

const ManageOrder = () => {
  const clickableHeader = {
    cursor: "pointer",
  };
  const location = useLocation();
  const history = useHistory();
  const [error, setError] = useState(null);
  const { promiseInProgress } = usePromiseTracker();
  const [tableData, setTableData] = useState(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("lastUpdatedOn");
  const [desc, setDesc] = useState(true);
  const [totalPage, setTotalPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [typingTimeOut, setTypingTimeout] = useState({ name: "", timeout: 0 });

  const pageSize = 10;

  const [filter, setFilter] = useState({
    orderStatus: "",
    orderId: ""
  });

  const colHeaders = [
    {
      displayName: "Order ID",
      name: "id",
      orderable: false,
    },
    {
      displayName: "Business Name",
      name: "LicensedBusinessPaymentInformation.LicensedCompanyName",
      orderable: false,
    },
    {
      displayName: "Region",
      name: "LicensedBusinessPaymentInformation.Region",
      orderable: false,
    },
    { displayName: "Created On", name: "createdOn", orderable: true },
    { displayName: "Last Updated On", name: "lastUpdatedOn", orderable: true },
    {
      displayName: "Last Updated By",
      name: "lastUpdatedBy",
      orderable: true,
    },
    {
      displayName: "Payment Mode",
      name: "paymentModeName",
      orderable: false,
    },
    {
      displayName: "Order Status",
      name: "orderStatusName",
      orderable: false,
    },
    {
      displayName: "",
      name: "action",
      orderable: false,
    },
  ];

  function handleSelectOrderStatus(e) {
    setPage(1);
    setFilter({ ...filter, orderStatus: e.target.value });
    let searchFilter = processParams(filter);
    searchFilter.orderStatus = parseInt(e.target.value);
    fetchData(1, sortBy, desc, searchFilter);
  }

  const handleSortChange = async (sortBy = null, desc = false) => {
    fetchData(page, sortBy, desc, filter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(newPage, sortBy, desc, filter);
  };

  const handleClickRedirect = (e) => {
    let id = e.currentTarget.dataset.id;
    history.push({
      pathname: AdminRoutes.MANAGESUBSCRIPTION_ORDER_DETAILS,
      state: {
        orderId: id,
      },
    });
  };

  function handleSearchOrderIdChange(orderId) {
    setPage(1);
    let searchFilter = processParams(filter);
    searchFilter.orderId = orderId;
    fetchData(1, sortBy, desc, searchFilter);
  }

  const handleKeyPress = (event) => {
    setFilter({ ...filter, orderId: event.target.value });
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearchOrderIdChange(event.target.value);
      clearTimeout(typingTimeOut.timeout);
    }
  };

  const handleAutoSearch = (orderId) => {
    setFilter({ ...filter, orderId: orderId });
    if (typingTimeOut.timeout) {
      clearTimeout(typingTimeOut.timeout);
    }
    setTypingTimeout({
      name: orderId,
      timeout: setTimeout(function () {
        handleSearchOrderIdChange(orderId);
      }, 1500),
    });
  };

  function processParams(filter) {
    if (filter === null) return null;
    let process = cloneDeep(filter);
    return process;
  }

  const fetchData = async (page, sortBy, desc, filter) => {
    let skipCount = (page - 1) * pageSize;
    trackPromise(
      adminService
        .SearchOrder(skipCount, sortBy, desc, filter)
        .then((result) => {
          setTableData(result.data.items);
          setTotalCount(result.data.totalCount);
          setTotalPage(Math.ceil(result.data.totalCount / pageSize));
        })
        .catch((error) => setError(error))
    );
  };

  useEffect(() => {
    fetchData(1, sortBy, desc, filter);
  }, []);

  if (error) {
    return <div>Error: {error.message}</div>;
  } else {
    return (
      <GlobalAlertContext.Consumer>
        {(alertObj) => (
          <>
            <Grid>
              <Row>
                <Col>
                  <Typography tag="h1">Manage Orders</Typography>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Select
                    label="Order Status"
                    defaultValue=""
                    onChange={handleSelectOrderStatus}
                  >
                    <option>All</option>
                    <option value="0">Pending</option>
                    <option value="1">Completed</option>
                    <option value="2">Canceled</option>
                  </Select>
                </Col>
                <Col lg={6} md={8} sm={8} alignEnd textAlign="right" noRightGutter={true}>
                  <Form>
                    <FormControl position="right" space="fifty" style={{ marginTop: "20px" }}>
                      <Input
                        name="search"
                        value={filter.orderId}
                        helpText="Search Order ID"
                        showHelpText
                        onKeyPress={handleKeyPress}
                        onChange={(e) => handleAutoSearch(e.target.value)}
                      >
                        <InputOption>
                          <Icon
                            name="search"
                            resolution="low"
                            style={clickableHeader}
                          />
                        </InputOption>
                      </Input>
                    </FormControl>
                  </Form>
                </Col>
              </Row>
              {promiseInProgress && <MCSLoading />}
              {!promiseInProgress && tableData && (
                <>
                  <Row>
                    <Col>
                      <MCSPagedTable
                        size={"compact"}
                        colHeaders={colHeaders}
                        useSearchBar={false}
                        usePagination={true}
                        selectedPage={page}
                        setSelectedPage={handlePageChange}
                        totalPages={totalPage}
                        ordered={sortBy}
                        setOrdered={setSortBy}
                        sortData={handleSortChange}
                        isDsc={desc}
                        setDsc={setDesc}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        tableData={
                          tableData &&
                          tableData.map((row, key) => (
                            <Tr key={key}>
                              <Td>{row.id}</Td>
                              <Td>{row.licensedBusinessPaymentInformation && row.licensedBusinessPaymentInformation.licensedCompanyName}</Td>
                              <Td>{row.licensedBusinessPaymentInformation && row.licensedBusinessPaymentInformation.region}</Td>
                              <Td>
                                {DateHelper.ToLocaleDateString(row.createdOn)}
                              </Td>
                              <Td>
                                {DateHelper.ToLocaleDateString(
                                  row.lastUpdatedOn
                                )}
                              </Td>
                              <Td>{row.lastUpdatedBy}</Td>
                              <Td>{row.paymentModeName}</Td>
                              <Td class="word-nobreak">
                                <OrderStatus order={row} />
                              </Td>
                              <Td>
                                <Icon
                                  data-id={row.id}
                                  onClick={handleClickRedirect}
                                  className="vss-clickable-icon"
                                  name="settings"
                                  resolution="low"
                                />
                              </Td>
                            </Tr>
                          ))
                        }
                      />
                    </Col>
                  </Row>
                </>
              )}
            </Grid>
          </>
        )}
      </GlobalAlertContext.Consumer>
    );
  }
};
export default ManageOrder;
